import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { useApi, BUSINESS, money, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/portal/StatusBadge";

export default function PrintDoc() {
  const { kind, id } = useParams();
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => {
    api("get", `/${kind}/${id}`).then(setD).catch(() => setD(false));
  }, [api, kind, id]);

  if (d === false) return <p className="p-10" data-testid="doc-not-found">Document not found.</p>;
  if (!d) return null;
  const title = kind === "estimates" ? "Estimate" : "Invoice";
  return (
    <div className="min-h-screen bg-[#F2EFE9] py-8 print:bg-white print:py-0" data-testid="print-doc-page">
      <div className="no-print mx-auto mb-4 flex max-w-3xl justify-between px-4">
        <Link to={`/portal/${kind}`} className="text-sm font-semibold text-[#A67C00]" data-testid="print-back-link">← Back</Link>
        <button onClick={() => window.print()} data-testid="print-btn" className="inline-flex items-center gap-2 rounded-full bg-[#0E1F3F] px-4 py-2 text-sm font-semibold text-white"><Printer size={15} />Print / Save PDF</button>
      </div>
      <div className="mx-auto max-w-3xl bg-white p-8 shadow-sm sm:p-12 print:shadow-none">
        <div className="flex flex-col justify-between gap-6 border-b pb-6 sm:flex-row">
          <div className="flex items-start gap-4">
            <img src="/brand/logo.png" alt="Firm Foundations logo" className="h-20 w-auto rounded-md" data-testid="print-logo" />
            <div>
            <p className="font-display text-2xl font-bold">{BUSINESS.name} LLC</p>
            <p className="mt-1 text-sm text-[#5C5850]">{BUSINESS.address}<br />{BUSINESS.phone} · {BUSINESS.email}</p>
            </div>
          </div>
          <div className="sm:text-right">
            <p className="font-display text-3xl font-bold text-[#A67C00]">{title}</p>
            <p className="font-mono-num text-sm" data-testid="print-doc-number">{d.number}</p>
            <p className="text-sm text-[#5C5850]">{fmtDate(d.created_at)}</p>
            <div className="mt-1"><StatusBadge status={d.status} /></div>
          </div>
        </div>
        <div className="grid gap-4 py-6 text-sm sm:grid-cols-2">
          <div><p className="eyebrow mb-1">Bill to</p><p className="font-semibold">{d.customer_name}</p><p>{d.customer_email}</p></div>
          <div className="sm:text-right"><p className="eyebrow mb-1">Project</p><p className="font-semibold">{d.project_name || "—"}</p>{d.title && <p>{d.title}</p>}{d.due_date && <p>Due {fmtDate(d.due_date)}</p>}</div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-y text-left text-xs uppercase tracking-wider text-[#8C877D]"><th className="py-2">Description</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Unit</th><th className="py-2 text-right">Amount</th></tr></thead>
          <tbody className="font-mono-num">
            {d.items.map((i, k) => (
              <tr key={k} className="border-b"><td className="py-2.5 font-sans">{i.description}</td><td className="py-2.5 text-right">{i.quantity}</td><td className="py-2.5 text-right">{money(i.unit_price)}</td><td className="py-2.5 text-right">{money(i.quantity * i.unit_price)}</td></tr>
            ))}
          </tbody>
        </table>
        <div className="ml-auto mt-4 w-64 space-y-1 font-mono-num text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{money(d.subtotal)}</span></div>
          <div className="flex justify-between"><span>Tax ({d.tax_rate}%)</span><span>{money(d.tax)}</span></div>
          <div className="flex justify-between border-t pt-2 text-lg font-semibold"><span>Total</span><span data-testid="print-doc-total">{money(d.total)}</span></div>
        </div>
        {d.notes && <p className="mt-8 whitespace-pre-wrap border-t pt-4 text-sm text-[#5C5850]">{d.notes}</p>}
        <p className="mt-10 text-center text-xs text-[#8C877D]">Thank you for building with {BUSINESS.name}.</p>
      </div>
    </div>
  );
}
